<template>
  <div
    v-if="!isBinary && !isPlatformElementInAutoGrade"
    :class="{
      'epm-Wrapper-start-target': isStartTarget,
      'epm-Wrapper-readonly': isDisabled
    }"
    :data-auto-testid="PROGRESS_MILESTONES_WRAPPER_TEST_ID"
    :data-testid="PROGRESS_MILESTONES_WRAPPER_TEST_ID"
    class="epm-Wrapper"
  >
    <div
      v-if="!isStartTarget"
      :data-auto-testid="PERIOD_SWITCH_WRAPPER_TEST_ID"
      :data-testid="PERIOD_SWITCH_WRAPPER_TEST_ID"
      class="epm-PeriodsSwitch"
    >
      <div class="epm-PeriodsSwitch_Title">
        <AppIcon class="" height="24" icon-name="milestones-periods" width="24" />
        {{ t('element_progress.split.title') }}
      </div>

      <AppToggle
        v-model="splitByPeriods"
        :data-auto-testid="PERIOD_SWITCH_TEST_ID"
        :data-testid="PERIOD_SWITCH_TEST_ID"
        :disabled="isDisabled"
        :size="TOGGLE_SIZES.LG"
        @update:model-value="onSplitByPeriodsUpdate"
      />
    </div>

    <template v-if="isShowPeriods">
      <div
        v-if="!isStartTarget"
        :data-auto-testid="PROGRESS_SETTINGS_WRAPPER_TEST_ID"
        :data-testid="PROGRESS_SETTINGS_WRAPPER_TEST_ID"
        class="epm-ProgressSettings"
      >
        <FormFieldNext :disabled="isDisabled" :label="t('field.period.title')">
          <AppSelect
            v-model="granulationId"
            :data-auto-testid="GRANULATION_SELECT_TEST_ID"
            :data-testid="GRANULATION_SELECT_TEST_ID"
            :disabled="isDisabled"
            :offset="[0, 0]"
            :options="thresholdPeriodGranulation"
            :type="SELECT_TYPES.MODERN"
            @update:model-value="onGranulationUpdate"
          />
        </FormFieldNext>

        <FormFieldNext :disabled="isDisabled" :label="t('element_progress.aggregation.label')">
          <AppSelect
            v-model="progressAggregationTypeId"
            :data-auto-testid="PERIOD_AGGREGATION_SELECT_TEST_ID"
            :data-testid="PERIOD_AGGREGATION_SELECT_TEST_ID"
            :disabled="isDisabled"
            :offset="[0, 0]"
            :options="thresholdProgressAggregations"
            :type="SELECT_TYPES.MODERN"
            @update:model-value="onUpdateProgressAggregation"
          />
        </FormFieldNext>
      </div>

      <template v-if="!isEmpty(milestones)">
        <AppDivider v-if="!isStartTarget" border-width="2px" class="epm-Divider" no-margin />

        <div
          :data-auto-testid="MILESTONES_LIST_HEADER_TEST_ID"
          :data-testid="MILESTONES_LIST_HEADER_TEST_ID"
          class="epm-ProgressHeader"
        >
          <div class="epm-ProgressHeader_Title">
            <AppIcon
              v-if="attrsByFieldTypeId.icon"
              :icon-name="attrsByFieldTypeId.icon"
              height="24"
              width="24"
            />

            {{ attrsByFieldTypeId.title }}
          </div>

          <DropdownMenu
            v-if="!isDisabled"
            :items="milestonesMenuItems"
            :offset="[0, 0]"
            :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
            data-testid="qwe"
            preferred-position="bottom-end"
            @item-click="onMilestonesMenuClick"
          >
            <template #activator="{ isOpened }">
              <AppButton
                :class="{ 'epm-ActionsButton-active': isOpened }"
                class="epm-ActionsButton"
                icon="more-next"
                size="sm"
                type="subtle"
              />
            </template>
          </DropdownMenu>
        </div>

        <div v-if="!isEmpty(milestones)" class="epm-ProgressMilestonesHeader">
          <FormFieldNext :disabled="isDisabled" :label="t('common.date')" />

          <FormFieldNext :disabled="isDisabled" :label="attrsByFieldTypeId.resolvedMilestoneName" />
        </div>
        <div
          v-if="!milestonesListState.listWasReplaced"
          v-auto-animate="{ duration: ELEMENT_FORM_ANIMATIONS_DURATION }"
          :data-auto-testid="MILESTONES_LIST_BODY_TEST_ID"
          :data-testid="MILESTONES_LIST_BODY_TEST_ID"
          :style="{
            '--cancel-animation-delay': `${CANCEL_ANIMATION_DELAY}ms`
          }"
          class="epm-ProgressMilestones"
        >
          <!--          <transition-group name="flip-list">-->
          <div
            v-for="milestone in milestones"
            :key="milestone.id"
            :class="{
              'epm-Milestone-moving': movingMilestone === milestone.id
            }"
            :data-auto-testid="MILESTONE_ITEM_TEST_ID"
            :data-testid="MILESTONE_ITEM_TEST_ID"
            class="epm-Milestone"
          >
            <AppVueDatePicker
              v-model="milestone[TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]"
              :data-auto-testid="MILESTONE_DATE_PICKER_TEST_ID"
              :data-testid="MILESTONE_DATE_PICKER_TEST_ID"
              :read-only="isDisabled"
              v-bind="getPickerAttrs(milestone.id)"
              @update:model-value="onDateUpdate($event, milestone.id)"
            />

            <div class="emp-Milestone_TargetValueWrapper">
              <AppInputNumberNext
                :key="getInputKey(milestone.id)"
                :ref="
                  el =>
                    registerTargetValueInputRef({
                      el,
                      id: milestone.id
                    })
                "
                v-model="milestone[TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]"
                :data-auto-testid="MILESTONE_TARGET_VALUE_TEST_ID"
                :data-testid="MILESTONE_TARGET_VALUE_TEST_ID"
                :digit-max-length="NUMBER_INPUT_DIGITS.ELEMENT_VALUES"
                :fraction="NUMBER_INPUT_FRACTIONS.ELEMENT_VALUES"
                :readonly="isDisabled"
                :suffix="unit"
                allow-negative
                class="epm-TargetValueInput"
                @blur="sendUpdate(true)"
              />

              <div v-if="!isDisabled" class="emp-Milestone_RemoveWrapper">
                <AppButton
                  :data-auto-testid="REMOVE_MILESTONE_BUTTON_TEST_ID"
                  :data-testid="REMOVE_MILESTONE_BUTTON_TEST_ID"
                  icon="close-common"
                  size="sm"
                  type="ghost"
                  @click="removeMilestone(milestone.id)"
                />
              </div>
            </div>
          </div>
          <!--          </transition-group>-->
        </div>
      </template>

      <div class="epm-Footer">
        <AppTableCreateButton
          v-if="!isDisabled && !isLimitReached"
          :data-auto-testid="ADD_MILESTONE_BUTTON_TEST_ID"
          :data-testid="ADD_MILESTONE_BUTTON_TEST_ID"
          full-width
          no-hover
          no-padding
          v-bind="addTableCreateButtonAttrs"
          @click="addMilestone"
        >
          {{ attrsByFieldTypeId.addButtonText }}
        </AppTableCreateButton>
      </div>
    </template>
  </div>
</template>

<script setup>
import { vAutoAnimate } from '@formkit/auto-animate'
import dayjs from 'dayjs'
import { cloneDeep, debounce, isEmpty, isEqual, isNull, omit } from 'lodash'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { tracker } from '@/tracking/amplitude'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import {
  NUMBER_INPUT_DIGITS,
  NUMBER_INPUT_FRACTIONS
} from '@/utils/components-configurations/app-input-number'
import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { TOGGLE_SIZES } from '@/utils/components-configurations/app-toggle'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import {
  DATE_UNITS,
  ELEMENT_PERIODS_DATE_FORMAT,
  getDatesInBetween,
  getMonthStartDateFromObject,
  MILESTONES_MAX_DATE
} from '@/utils/date'
import { MENU_ITEMS_GROUPS } from '@/utils/dropdown-menu'
import { usePeriodGranulation } from '@/utils/element-progress'
import { REQUEST_ENTITY_KEYS, TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import { NOTIFICATION_TYPES, showNotify } from '@/utils/notify'
import { isSplitByPeriodsEnabled } from '@/utils/objectives'
import {
  AUTO_GRADE_OPTION_VALUE,
  DEFAULT_MILESTONES_SETTINGS,
  DEFAULT_THRESHOLD_PERIOD_GRANULATION,
  DEFAULT_THRESHOLD_PROGRESS_AGGREGATION,
  findPeriodGranulationOptionsByTypeId,
  findProgressAggregationOptionsByTypeId,
  getProgressOptionByFieldTypeId,
  getResultTypeSettingsDisabledStates,
  isBinaryProgress,
  isStartTargetProgress,
  isThresholdProgress,
  THRESHOLD_PERIOD_GRANULATION,
  THRESHOLD_PROGRESS_AGGREGATIONS
} from '@/utils/okr-element-values'
import {
  ELEMENT_FORM_ANIMATIONS_DURATION,
  isMilestonesSettingsChanged
} from '@/utils/okr-elements-forms-helpers'
import { includesValue } from '@/utils/prop-validators'
import { uid } from '@/utils/uid'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import AppVueDatePicker from '@/components/form/AppVueDatePicker'
import FormFieldNext from '@/components/form/FormFieldNext'
import {
  ADD_MILESTONE_BUTTON_TEST_ID,
  GRANULATION_SELECT_TEST_ID,
  MILESTONE_DATE_PICKER_TEST_ID,
  MILESTONE_ITEM_TEST_ID,
  MILESTONE_TARGET_VALUE_TEST_ID,
  MILESTONES_LIST_BODY_TEST_ID,
  MILESTONES_LIST_HEADER_TEST_ID,
  PERIOD_AGGREGATION_SELECT_TEST_ID,
  PERIOD_SWITCH_TEST_ID,
  PERIOD_SWITCH_WRAPPER_TEST_ID,
  PROGRESS_MILESTONES_WRAPPER_TEST_ID,
  PROGRESS_SETTINGS_WRAPPER_TEST_ID,
  REMOVE_MILESTONE_BUTTON_TEST_ID
} from '@/components/form/jest-helpers'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInputNumberNext from '@/components/ui/AppInputNumberNext/AppInputNumberNext'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import AppToggle from '@/components/ui/AppToggle/AppToggle'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'

defineOptions({
  name: 'ElementProgressMilestones'
})

const { ELEMENT_TARGET_PERIODS, PROGRESS_AGGREGATION_TYPE_ID, GRANULATION_ID } = OKR_ELEMENT_KEYS

const CANCEL_ANIMATION_DELAY = ELEMENT_FORM_ANIMATIONS_DURATION * 10

const props = defineProps({
  targetValue: {
    type: [Number, String],
    default: 0
  },

  unit: {
    type: String,
    default: ''
  },

  fieldTypeId: {
    type: [String, Number],
    default: KR_VALUE_TYPES.START_TARGET,
    validator: v => includesValue(v, { ...KR_VALUE_TYPES, AUTO_GRADE_OPTION_VALUE })
  },

  isPlatformElementInAutoGrade: {
    type: Boolean
  },

  milestonesSettings: {
    type: Object,
    default: () => ({})
  },

  isEdit: {
    type: Boolean
  },

  isMetric: {
    type: Boolean
  },

  editable: {
    type: Boolean
  },

  valuesAreReadonly: {
    type: Boolean
  },

  isLockedOnPercent: {
    type: Boolean
  },

  isBinarySelectDisabled: {
    type: Boolean
  }
})

const { t } = useI18n()

const { getPeriodGranulationOptions: thresholdPeriodGranulation } = usePeriodGranulation()

const thresholdProgressAggregations = computed(() => {
  return Object.values(THRESHOLD_PROGRESS_AGGREGATIONS).map((item, index) => {
    return {
      ...item,
      label: t(`element_progress_aggregation_values[${index}]`)
    }
  })
})

const SPLIT_OPTIONS = {
  OFF: {
    value: false,
    label: t('action.off')
  },
  ON: {
    value: true,
    label: t('action.on')
  }
}

const isStartTarget = computed(() => {
  return isStartTargetProgress({
    element: {
      [REQUEST_ENTITY_KEYS.FIELD_TYPE_ID]: props.fieldTypeId
    }
  })
})

const isThreshold = computed(() => {
  return isThresholdProgress({
    element: {
      [REQUEST_ENTITY_KEYS.FIELD_TYPE_ID]: props.fieldTypeId
    }
  })
})

const isBinary = computed(() => {
  return isBinaryProgress({
    element: {
      [REQUEST_ENTITY_KEYS.FIELD_TYPE_ID]: props.fieldTypeId
    }
  })
})

const splitByPeriods = ref(SPLIT_OPTIONS.OFF.value)

const isShowPeriods = computed(() => {
  return isStartTarget.value || splitByPeriods.value
})

const granulationId = ref(DEFAULT_THRESHOLD_PERIOD_GRANULATION)
const progressAggregationTypeId = ref(DEFAULT_THRESHOLD_PROGRESS_AGGREGATION)

const LIMIT = 53

const isLimitReached = computed(() => milestones.value.length >= LIMIT)

const MILESTONES_MENU_ITEMS = [
  {
    name: ACTIONS_KEYS.ADD,
    group: MENU_ITEMS_GROUPS.CREATING,
    icon: 'plus-next'
  },
  {
    name: ACTIONS_KEYS.REMOVE,
    group: MENU_ITEMS_GROUPS.REMOVING,
    icon: 'delete-next',
    color: 'var(--grade-low-color-next)'
  }
]

const milestonesMenuItems = computed(() => {
  const normalizedMenuItems = MILESTONES_MENU_ITEMS.map(item => {
    let label = ''

    if (item.name === ACTIONS_KEYS.ADD) {
      label = attrsByFieldTypeId.value.addButtonText
    }

    if (item.name === ACTIONS_KEYS.REMOVE) {
      label = attrsByFieldTypeId.value.removeAllButtonText
    }

    return {
      ...item,
      label
    }
  })

  if (isLimitReached.value) {
    return normalizedMenuItems.filter(({ name }) => name !== ACTIONS_KEYS.ADD)
  }

  return normalizedMenuItems
})

const { WEEK, MONTH, QUARTER, DAY } = DATE_UNITS

const startTargetFormModel = ref([])

const thresholdFormModel = ref({
  [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: [],
  [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: [],
  [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: []
})

const sortByDateFn = () => {
  if (isThreshold.value && granulationId.value === THRESHOLD_PERIOD_GRANULATION.MONTH.value) {
    return (a, b) => {
      return dayjs(getMonthStartDateFromObject(a[TARGET_PERIOD_ENTITY_KEYS.INNER_DATE])).diff(
        dayjs(getMonthStartDateFromObject(b[TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]))
      )
    }
  }

  return (a, b) => {
    const getInnerDate = innerDate => (Array.isArray(innerDate) ? innerDate[0] : innerDate)

    return dayjs(getInnerDate(a[TARGET_PERIOD_ENTITY_KEYS.INNER_DATE])).diff(
      dayjs(getInnerDate(b[TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]))
    )
  }
}

const milestonesListState = reactive({
  listWasReplaced: false,
  toggle() {
    if (this.listWasReplaced) {
      nextTick(() => {
        this.listWasReplaced = false
      })
    } else {
      this.listWasReplaced = true
    }
  }
})

watch(
  () => props.fieldTypeId,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      milestonesListState.toggle()

      milestonesListState.toggle()
    }
  },
  {
    immediate: true
  }
)

const shoudSendEvents = computed(() => {
  return (
    props.isEdit &&
    props.isMetric &&
    props.editable &&
    !props.valuesAreReadonly &&
    !props.isLockedOnPercent
  )
})

const milestones = computed(() => {
  if (isBinary.value || props.isPlatformElementInAutoGrade) {
    return []
  }

  const modelByFieldTypeId = {
    [KR_VALUE_TYPES.START_TARGET]: startTargetFormModel.value,
    [KR_VALUE_TYPES.THRESHOLD]: thresholdFormModel.value[granulationId.value]
  }

  const milestones = modelByFieldTypeId[props.fieldTypeId]

  if (!milestones) {
    throw new Error('Unknown fieldTypeId')
  }

  return milestones
})

const trackRemoveMilestoneEvent = () => {
  if (!shoudSendEvents.value) return
  let trackingEventName = 'metric milestone removed'
  const trackingPayload = {}

  if (isStartTarget.value) {
    trackingPayload.total_milestones = startTargetFormModel.value.length
  } else {
    trackingEventName = 'unique threshold removed'
  }

  tracker.logEvent(trackingEventName, trackingPayload)
}

const removeMilestone = id => {
  if (isStartTarget.value) {
    startTargetFormModel.value = startTargetFormModel.value.filter(milestone => milestone.id !== id)
  } else {
    thresholdFormModel.value[granulationId.value] = thresholdFormModel.value[
      granulationId.value
    ].filter(milestone => milestone.id !== id)
  }

  sendUpdate()

  trackRemoveMilestoneEvent()
}

const showInfiniteLoopMessage = () => {
  showNotify({
    type: NOTIFICATION_TYPES.WARNING,
    title: t('notifications.oops'),
    content:
      "Something went wrong. We can't add more milestones. Please try to reload the page and add milestones again."
  })
}

class LoopIterator {
  constructor() {
    this.limit = LIMIT
    this.iterationsCount = 0
  }

  get canContinue() {
    return this.iterationsCount < this.limit
  }

  addIteration() {
    if (!this.canContinue) return
    this.iterationsCount += 1
  }
}

const createNewDay = () => {
  if (isEmpty(milestones.value)) {
    return dayjs().format(ELEMENT_PERIODS_DATE_FORMAT)
  }

  const allSelectedDays = milestones.value.map(({ innerDate }) => dayjs(innerDate))

  const maxSelectedDay = dayjs.max(allSelectedDays)

  let nextDay = dayjs(maxSelectedDay).add(1, DAY)

  if (nextDay >= MILESTONES_MAX_DATE) {
    nextDay = dayjs(MILESTONES_MAX_DATE).startOf(DAY)

    const formattedSelectedDays = new Set(
      allSelectedDays.map(date => date.format(ELEMENT_PERIODS_DATE_FORMAT))
    )

    const iterator = new LoopIterator()

    do {
      if (!iterator.canContinue) {
        nextDay = null
        showInfiniteLoopMessage()
        break
      }
      nextDay = nextDay.subtract(1, DAY)
      iterator.addIteration()
    } while (formattedSelectedDays.has(nextDay.format(ELEMENT_PERIODS_DATE_FORMAT)))
  }

  return nextDay?.format(ELEMENT_PERIODS_DATE_FORMAT)
}

const createNewWeek = () => {
  if (isEmpty(milestones.value)) {
    return [dayjs().startOf(WEEK), dayjs().endOf(WEEK)].map(date =>
      date.format(ELEMENT_PERIODS_DATE_FORMAT)
    )
  }

  const startDatesOfSelectedWeeks = milestones.value.map(({ innerDate }) => dayjs(innerDate[0]))

  const maxSelectedWeek = dayjs.max(startDatesOfSelectedWeeks)
  let nextWeekStartDate = dayjs(maxSelectedWeek).add(1, WEEK).startOf(WEEK)

  if (nextWeekStartDate >= MILESTONES_MAX_DATE) {
    nextWeekStartDate = dayjs(MILESTONES_MAX_DATE).startOf(WEEK)

    const formattedSelectedWeeks = new Set(
      startDatesOfSelectedWeeks.map(date => date.format(ELEMENT_PERIODS_DATE_FORMAT))
    )

    const iterator = new LoopIterator()

    do {
      if (!iterator.canContinue) {
        nextWeekStartDate = null
        showInfiniteLoopMessage()
        break
      }

      nextWeekStartDate = nextWeekStartDate.subtract(1, WEEK)
      iterator.addIteration()
    } while (formattedSelectedWeeks.has(nextWeekStartDate.format(ELEMENT_PERIODS_DATE_FORMAT)))
  }

  if (!nextWeekStartDate) return null

  return [nextWeekStartDate, nextWeekStartDate.endOf(WEEK)].map(date =>
    date.format(ELEMENT_PERIODS_DATE_FORMAT)
  )
}

const createNewMonth = () => {
  if (isEmpty(milestones.value)) {
    return {
      [TARGET_PERIOD_ENTITY_KEYS.YEAR]: dayjs().year(),
      [TARGET_PERIOD_ENTITY_KEYS.MONTH]: dayjs().month()
    }
  }

  const startDatesOfSelectedMonths = milestones.value.map(({ innerDate }) =>
    dayjs(getMonthStartDateFromObject(innerDate))
  )

  const maxSelectedMonth = dayjs.max(startDatesOfSelectedMonths)

  let nextMonthStartDate = dayjs(maxSelectedMonth).add(1, MONTH).startOf(MONTH)

  if (nextMonthStartDate >= MILESTONES_MAX_DATE) {
    nextMonthStartDate = dayjs(MILESTONES_MAX_DATE).startOf(MONTH)

    const formattedSelectedMonths = new Set(
      startDatesOfSelectedMonths.map(date => date.format(ELEMENT_PERIODS_DATE_FORMAT))
    )

    const iterator = new LoopIterator()

    do {
      if (!iterator.canContinue) {
        nextMonthStartDate = null
        showInfiniteLoopMessage()
        break
      }

      nextMonthStartDate = nextMonthStartDate.subtract(1, MONTH)
      iterator.addIteration()
    } while (formattedSelectedMonths.has(nextMonthStartDate.format(ELEMENT_PERIODS_DATE_FORMAT)))
  }

  if (!nextMonthStartDate) return null

  return {
    [TARGET_PERIOD_ENTITY_KEYS.YEAR]: nextMonthStartDate.year(),
    [TARGET_PERIOD_ENTITY_KEYS.MONTH]: nextMonthStartDate.month()
  }
}

const createNewQuarter = () => {
  if (isEmpty(milestones.value)) {
    return dayjs().startOf(QUARTER).format(ELEMENT_PERIODS_DATE_FORMAT)
  }

  const allSelectedQuarters = milestones.value.map(({ innerDate }) => dayjs(innerDate))
  const maxSelectedQuarter = dayjs.max(allSelectedQuarters)
  let nextQuarter = dayjs(maxSelectedQuarter).add(1, QUARTER).startOf(QUARTER)

  if (nextQuarter >= MILESTONES_MAX_DATE) {
    nextQuarter = dayjs(MILESTONES_MAX_DATE).startOf(QUARTER)

    const formattedSelectedQuarters = new Set(
      allSelectedQuarters.map(date => date.format(ELEMENT_PERIODS_DATE_FORMAT))
    )

    const iterator = new LoopIterator()

    do {
      if (!iterator.canContinue) {
        nextQuarter = null
        showInfiniteLoopMessage()
        break
      }
      nextQuarter = nextQuarter.subtract(1, QUARTER)
      iterator.addIteration()
    } while (formattedSelectedQuarters.has(nextQuarter.format(ELEMENT_PERIODS_DATE_FORMAT)))
  }

  return nextQuarter?.format(ELEMENT_PERIODS_DATE_FORMAT)
}

const targetValueInputsReferences = ref({})

const registerTargetValueInputRef = ({ id, el }) => {
  if (!el) {
    delete targetValueInputsReferences.value[id]
    return
  }

  targetValueInputsReferences.value[id] = el
}

const focusMilestoneInput = (milestoneId = null) => {
  if (!milestoneId) return

  nextTick(() => {
    targetValueInputsReferences.value[milestoneId]?.focus()
  })
}

const trackAddMilestoneEvent = () => {
  if (!shoudSendEvents.value) return

  let trackingEventName = 'metric milestone added'
  const trackingPayload = {}

  if (isStartTarget.value) {
    trackingPayload.total_milestones = startTargetFormModel.value.length
  } else {
    trackingEventName = 'unique threshold set'

    const { label } = findPeriodGranulationOptionsByTypeId({
      typeId: granulationId.value
    })

    trackingPayload.threshold_split_period = t(label, 1, {
      locale: 'en'
    }).toLowerCase()
  }

  tracker.logEvent(trackingEventName, trackingPayload)
}

const addMilestone = () => {
  removeMovingMilestone()

  let newEntity = null

  if (isStartTarget.value) {
    newEntity = createNewDay()
  } else {
    const createEntityByGranulationId = {
      [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: createNewWeek,
      [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: createNewMonth,
      [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: createNewQuarter
    }

    newEntity = createEntityByGranulationId[granulationId.value]()
  }

  if (!newEntity) return

  const newMilestone = {
    [TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]: newEntity,
    [TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]: Number(props.targetValue) || 0,
    [TARGET_PERIOD_ENTITY_KEYS.ID]: uid()
  }

  if (isStartTarget.value) {
    startTargetFormModel.value = [...startTargetFormModel.value, newMilestone].toSorted(
      sortByDateFn()
    )
  } else {
    thresholdFormModel.value[granulationId.value] = [
      ...thresholdFormModel.value[granulationId.value],
      newMilestone
    ].toSorted(sortByDateFn())
  }

  focusMilestoneInput(newMilestone.id)

  sendUpdate()

  trackAddMilestoneEvent()
}

const disabledDatesForDayPicker = computed(() => {
  if (isEmpty(milestones.value)) {
    return []
  }

  return milestones.value.reduce((acc, milestone) => {
    const allOtherSelectedDates = milestones.value
      .filter(({ id }) => id !== milestone.id)
      .map(({ innerDate }) => innerDate || null)
      .filter(Boolean)

    const { id } = milestone

    return {
      ...acc,
      [id]: allOtherSelectedDates
    }
  }, {})
})

const disabledDatesForWeekPickerCache = new Map()

const disabledDatesForWeekPicker = computed(() => {
  if (
    isEmpty(milestones.value) ||
    granulationId.value !== THRESHOLD_PERIOD_GRANULATION.WEEK.value
  ) {
    return []
  }

  const disabledDatesByEachMilestone = milestones.value.reduce((acc, milestone) => {
    const { id, innerDate } = milestone
    const [startDate, endDate] = innerDate

    const cacheKey = `${startDate}-${endDate}`

    let disabledDates = []

    if (disabledDatesForWeekPickerCache.has(cacheKey)) {
      disabledDates = disabledDatesForWeekPickerCache.get(cacheKey)
    } else {
      disabledDates = getDatesInBetween({
        startDate,
        endDate
      })
      disabledDatesForWeekPickerCache.set(cacheKey, disabledDates)
    }

    return {
      ...acc,
      [id]: disabledDates
    }
  }, {})

  return milestones.value.reduce((acc, milestone) => {
    const { id } = milestone

    const clone = cloneDeep(disabledDatesByEachMilestone)

    return {
      ...acc,
      [id]: Object.values(omit(clone, id)).flat()
    }
  }, {})
})

const disabledMonthsCache = new Map()

const disabledDatesForMonthPicker = computed(() => {
  if (
    isEmpty(milestones.value) ||
    granulationId.value !== THRESHOLD_PERIOD_GRANULATION.MONTH.value
  ) {
    return []
  }

  return milestones.value.reduce((acc, milestone) => {
    const allOtherSelectedMonths = milestones.value
      .filter(({ id }) => id !== milestone.id)
      .map(({ innerDate }) => {
        if (isEmpty(innerDate)) return null

        const { year, month } = innerDate

        const cacheKey = `${year}-${month}`

        if (disabledMonthsCache.has(cacheKey)) {
          return disabledMonthsCache.get(cacheKey)
        }

        const disabledDate = getMonthStartDateFromObject({
          year,
          month
        }).format(ELEMENT_PERIODS_DATE_FORMAT)

        disabledMonthsCache.set(cacheKey, disabledDate)
        return disabledDate
      })
      .filter(Boolean)

    const { id } = milestone

    return {
      ...acc,
      [id]: allOtherSelectedMonths
    }
  }, {})
})

const disabledDatesForQuarterPicker = computed(() => {
  if (
    isEmpty(milestones.value) ||
    granulationId.value !== THRESHOLD_PERIOD_GRANULATION.QUARTER.value
  ) {
    return []
  }

  return milestones.value.reduce((acc, milestone) => {
    const allOtherSelectedQuarters = milestones.value
      .filter(({ id }) => id !== milestone.id)
      .map(({ innerDate }) => innerDate || null)
      .filter(Boolean)

    const { id } = milestone

    return {
      ...acc,
      [id]: allOtherSelectedQuarters
    }
  }, {})
})

const getPickerAttrs = id => {
  if (isStartTarget.value) {
    return {
      disabledDates: disabledDatesForDayPicker.value[id]
    }
  }

  const pickerAttrsByGranulationId = {
    [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: {
      weekPicker: true,
      disabledDates: disabledDatesForWeekPicker.value[id]
    },
    [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: {
      monthPicker: true,
      disabledDates: disabledDatesForMonthPicker.value[id]
    },
    [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: {
      quarterPicker: true,
      disabledDates: disabledDatesForQuarterPicker.value[id]
    }
  }

  return pickerAttrsByGranulationId[granulationId.value] || {}
}

const onMilestonesMenuClick = name => {
  if (name === ACTIONS_KEYS.ADD) {
    addMilestone()
  }

  if (name === ACTIONS_KEYS.REMOVE) {
    if (isStartTarget.value) {
      startTargetFormModel.value = []
    } else {
      thresholdFormModel.value[granulationId.value] = []
    }

    sendUpdate()

    trackRemoveMilestoneEvent()
  }
}

const attrsByFieldTypeId = computed(() => {
  const resultTypeOption = getProgressOptionByFieldTypeId(props.fieldTypeId)

  const icon = isStartTarget.value ? 'milestones' : resultTypeOption.icon || null

  const { milestoneName } = resultTypeOption

  const resolvedMilestoneName = (qty = 0) => (milestoneName ? t(milestoneName, qty) : '')

  const title = isStartTarget.value
    ? resolvedMilestoneName()
    : t('common.unique_entity', { entity: resolvedMilestoneName().toLowerCase() })

  const addButtonText = t('common.add_entity', {
    entity: resolvedMilestoneName(1).toLowerCase()
  })

  const removeAllButtonText = t('common.clear_entities', {
    entity: resolvedMilestoneName().toLowerCase()
  })

  return {
    icon,
    title,
    addButtonText,
    removeAllButtonText,
    resolvedMilestoneName: resolvedMilestoneName(1)
  }
})

const addTableCreateButtonAttrs = computed(() => {
  if (isEmpty(milestones.value)) {
    return {
      class: 'epm-AddMilestone',
      iconAfter: 'plus-next',
      iconName: attrsByFieldTypeId.value.icon
    }
  }
  return {
    iconName: 'plus-next'
  }
})

const shouldParsePayload = ({ elementTargetPeriods = null } = {}) => {
  return !isNull(elementTargetPeriods) && Array.isArray(elementTargetPeriods)
}

const createOrParsePayloadForDays = ({ elementTargetPeriods = null } = {}) => {
  // here we are parsing payload from BE
  if (shouldParsePayload({ elementTargetPeriods })) {
    return cloneDeep(elementTargetPeriods).map(milestone => {
      const { endDate, targetValue } = milestone

      return {
        [TARGET_PERIOD_ENTITY_KEYS.ID]: uid(),
        [TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]: endDate,
        targetValue
      }
    })
  }

  // otherwise we are creating payload for BE
  return cloneDeep(milestones.value).map(milestone => {
    const { innerDate, targetValue } = milestone

    return {
      [TARGET_PERIOD_ENTITY_KEYS.END_DATE]: innerDate,
      targetValue
    }
  })
}

const createOrParsePayloadForWeeks = ({ elementTargetPeriods = null } = {}) => {
  // here we are parsing payload from BE
  if (shouldParsePayload({ elementTargetPeriods })) {
    return cloneDeep(elementTargetPeriods).map(milestone => {
      const { startDate, endDate, targetValue } = milestone

      return {
        [TARGET_PERIOD_ENTITY_KEYS.ID]: uid(),
        [TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]: [startDate, endDate],
        [TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]: Number(targetValue)
      }
    })
  }

  // otherwise we are creating payload for BE
  return cloneDeep(milestones.value).map(milestone => {
    const { innerDate, targetValue } = milestone
    const [startDate, endDate] = innerDate

    return {
      startDate,
      endDate,
      targetValue
    }
  })
}

const createOrParsePayloadForMonths = ({ elementTargetPeriods = null } = {}) => {
  // here we are parsing payload from BE
  if (shouldParsePayload({ elementTargetPeriods })) {
    return cloneDeep(elementTargetPeriods).map(milestone => {
      const { startDate, targetValue } = milestone

      return {
        [TARGET_PERIOD_ENTITY_KEYS.ID]: uid(),
        [TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]: {
          [TARGET_PERIOD_ENTITY_KEYS.YEAR]: dayjs(startDate).startOf(MONTH).year(),
          [TARGET_PERIOD_ENTITY_KEYS.MONTH]: dayjs(startDate).startOf(MONTH).month()
        },
        [TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]: Number(targetValue)
      }
    })
  }

  // otherwise we are creating payload for BE
  return cloneDeep(milestones.value).map(milestone => {
    const { innerDate, targetValue } = milestone
    const { year, month } = innerDate

    const startDate = getMonthStartDateFromObject({ year, month }).format(
      ELEMENT_PERIODS_DATE_FORMAT
    )
    const endDate = dayjs(startDate).endOf(MONTH).format(ELEMENT_PERIODS_DATE_FORMAT)

    return {
      startDate,
      endDate,
      [TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]: Number(targetValue)
    }
  })
}

const createOrParsePayloadForQuarters = ({ elementTargetPeriods = null } = {}) => {
  // here we are parsing payload from BE
  if (shouldParsePayload({ elementTargetPeriods })) {
    return cloneDeep(elementTargetPeriods).map(milestone => {
      const { startDate, targetValue } = milestone

      return {
        [TARGET_PERIOD_ENTITY_KEYS.ID]: uid(),
        [TARGET_PERIOD_ENTITY_KEYS.INNER_DATE]: startDate,
        [TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]: Number(targetValue)
      }
    })
  }

  // otherwise we are creating payload for BE
  return cloneDeep(milestones.value).map(milestone => {
    const { innerDate, targetValue } = milestone

    return {
      [TARGET_PERIOD_ENTITY_KEYS.START_DATE]: innerDate,
      [TARGET_PERIOD_ENTITY_KEYS.END_DATE]: dayjs(innerDate)
        .endOf(QUARTER)
        .format(ELEMENT_PERIODS_DATE_FORMAT),
      [TARGET_PERIOD_ENTITY_KEYS.TARGET_VALUE]: Number(targetValue)
    }
  })
}

const reset = () => {
  startTargetFormModel.value = []

  thresholdFormModel.value = {
    [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: [],
    [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: [],
    [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: []
  }

  splitByPeriods.value = SPLIT_OPTIONS.OFF.value

  granulationId.value = DEFAULT_THRESHOLD_PERIOD_GRANULATION
  progressAggregationTypeId.value = DEFAULT_THRESHOLD_PROGRESS_AGGREGATION
}

const applyEditState = () => {
  if (isEmpty(props.milestonesSettings) || isBinary.value || props.isPlatformElementInAutoGrade) {
    reset()
    return
  }

  if (isStartTarget.value) {
    startTargetFormModel.value = createOrParsePayloadForDays({
      elementTargetPeriods: props.milestonesSettings[ELEMENT_TARGET_PERIODS]
    })

    return
  }

  const {
    progressAggregationTypeId: savedProgressAggregationTypeId,
    granulationId: savedGranulationId
  } = props.milestonesSettings

  splitByPeriods.value = isSplitByPeriodsEnabled({
    milestonesSettings: props.milestonesSettings
  })

  if (!splitByPeriods.value) return

  progressAggregationTypeId.value = savedProgressAggregationTypeId
  granulationId.value = savedGranulationId

  const createOrParsePayloadByGranulationId = {
    [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: createOrParsePayloadForWeeks,
    [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: createOrParsePayloadForMonths,
    [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: createOrParsePayloadForQuarters
  }

  thresholdFormModel.value[granulationId.value] = createOrParsePayloadByGranulationId[
    granulationId.value
  ]({
    elementTargetPeriods: props.milestonesSettings[ELEMENT_TARGET_PERIODS]
  })
}

onMounted(() => {
  applyEditState()
})

const createPayload = () => {
  const commonPayload = cloneDeep(DEFAULT_MILESTONES_SETTINGS)

  if (isBinary.value || props.isPlatformElementInAutoGrade) {
    return commonPayload
  }

  if (isThreshold.value) {
    if (!splitByPeriods.value) {
      return commonPayload
    }

    const createPayloadByGranulationId = {
      [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: createOrParsePayloadForWeeks,
      [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: createOrParsePayloadForMonths,
      [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: createOrParsePayloadForQuarters
    }

    const elementTargetPeriods = createPayloadByGranulationId[granulationId.value]()

    return {
      ...commonPayload,
      elementTargetPeriods,
      [GRANULATION_ID]: granulationId.value,
      [PROGRESS_AGGREGATION_TYPE_ID]: progressAggregationTypeId.value
    }
  }

  if (isStartTarget.value) {
    const elementTargetPeriods = createOrParsePayloadForDays()

    return {
      ...commonPayload,
      elementTargetPeriods
    }
  }

  return commonPayload
}

/*
function freezeUI() {
  return new Promise(resolve => {
    const overlay = document.createElement('div')
    overlay.style.position = 'fixed'
    overlay.style.top = '0'
    overlay.style.left = '0'
    overlay.style.width = '100%'
    overlay.style.height = '100%'
    overlay.style.zIndex = '1000'
    overlay.style.display = 'flex'
    overlay.style.alignItems = 'center'
    overlay.style.justifyContent = 'center'
    overlay.style.color = 'white'
    overlay.style.fontSize = '20px'

    document.body.appendChild(overlay)

    overlay.addEventListener('click', () => {
      document.body.removeChild(overlay)
      resolve()
    })
  })
}
*/

/*
for FREEZE UI
movingMilestone.value = milestoneId
document.querySelectorAll('.epm-Milestone')[11].classList.add('stop')
freezeUI().then(() => {
  document.querySelectorAll('.epm-Milestone')[11].classList.remove('stop')
  startTargetFormModel.value = startTargetFormModel.value.toSorted(sortByDateFn())

  setTimeout(() => {
    movingMilestone.value = null
  }, ELEMENT_FORM_ANIMATIONS_DURATION)
})*/

const movingMilestone = ref(null)

const removeMovingMilestone = () => {
  if (movingMilestone.value) {
    movingMilestone.value = null
  }
}

const getSortedMilestones = async ({ source, milestoneId = null } = {}) => {
  if (!source || !milestoneId) {
    throw new Error('Unknown source or milestoneId')
  }

  const sortedList = source.toSorted(sortByDateFn())

  let result = cloneDeep(source)

  if (!isEqual(source, sortedList)) {
    if (milestoneId) {
      movingMilestone.value = milestoneId
    }

    result = sortedList

    await nextTick()

    setTimeout(() => {
      removeMovingMilestone()
    }, CANCEL_ANIMATION_DELAY)
  }

  return result
}

const onDateUpdate = async (_, milestoneId = null) => {
  removeMovingMilestone()
  await nextTick()

  if (isStartTarget.value) {
    startTargetFormModel.value = await getSortedMilestones({
      source: startTargetFormModel.value,
      milestoneId
    })
  } else {
    thresholdFormModel.value[granulationId.value] = await getSortedMilestones({
      source: thresholdFormModel.value[granulationId.value],
      milestoneId
    })
  }

  focusMilestoneInput(milestoneId)

  sendUpdate()
}

const sendUpdate = (sendImmediately = false) => {
  if (isDisabled.value) return

  if (props.isEdit && !props.editable) return

  debouncedSendUpdate()
  if (sendImmediately) {
    debouncedSendUpdate.flush()
  }
}

const onSplitByPeriodsUpdate = newValue => {
  sendUpdate()

  if (shoudSendEvents.value) {
    const verb = newValue ? 'enabled' : 'disabled'
    const eventName = `metric split ${verb}`
    const { label } = findPeriodGranulationOptionsByTypeId({
      typeId: granulationId.value
    })

    const trackingPayload = {
      threshold_split_period: t(label, 1, {
        locale: 'en'
      }).toLowerCase()
    }

    tracker.logEvent(eventName, trackingPayload)
  }
}

const emit = defineEmits({
  'update-milestones-settings': null,
  'update-progress-aggregation': null
})

const debouncedSendUpdate = debounce(async () => {
  const payload = createPayload()

  if (
    !isMilestonesSettingsChanged({
      modelValue: {
        ...props.milestonesSettings,
        [REQUEST_ENTITY_KEYS.FIELD_TYPE_ID]: props.fieldTypeId
      },
      milestonesSettings: payload
    })
  )
    return

  emit('update-milestones-settings', payload)
}, 300)

const onGranulationUpdate = newValue => {
  sendUpdate()

  if (shoudSendEvents.value) {
    const { label } = findPeriodGranulationOptionsByTypeId({
      typeId: newValue
    })

    tracker.logEvent('changed threshold split period', {
      threshold_split_period: t(label, 1, {
        locale: 'en'
      }).toLowerCase()
    })
  }
}

const onUpdateProgressAggregation = newValue => {
  if (newValue === props.milestonesSettings[OKR_ELEMENT_KEYS.PROGRESS_AGGREGATION_TYPE_ID]) return
  emit('update-progress-aggregation', newValue)

  if (shoudSendEvents.value) {
    const { trackingLabel } = findProgressAggregationOptionsByTypeId({
      typeId: newValue
    })

    tracker.logEvent('changed progress calculation option', {
      threshold_progress_calculation: trackingLabel
    })
  }
}

const getInputKey = id => {
  const defaultKey = `milestone-${id}-input`

  if (isStartTarget.value) {
    return defaultKey
  }

  return `${defaultKey}-granulation-${granulationId.value}`
}

const requestDataUpdate = ({ fieldTypeId = null } = {}) => {
  return new Promise((resolve, reject) => {
    if (
      !fieldTypeId ||
      ![...Object.values(KR_VALUE_TYPES), AUTO_GRADE_OPTION_VALUE].includes(fieldTypeId)
    ) {
      return reject(new Error('Unknown fieldTypeId'))
    }

    if (props.fieldTypeId === fieldTypeId) {
      return resolve(createPayload())
    }

    let stop
    const watchHandler = watch(
      () => props.fieldTypeId,
      (newValue, oldValue) => {
        if (newValue === fieldTypeId && newValue !== oldValue) {
          stop?.()
          resolve(createPayload())
        }
      }
    )
    stop = watchHandler.stop
  })
}

const restoreDefaults = () => {
  if (!props.isEdit) {
    reset()
  }
}

const forceUpdate = async () => {
  await new Promise(resolve => {
    const { stop } = watch(
      () => props.milestonesSettings,
      (newValue, oldValue) => {
        if (props.isEdit && isEmpty(oldValue) && !isEmpty(newValue)) {
          milestonesListState.toggle()
          reset()
          applyEditState()
          stop()
          resolve()
          milestonesListState.toggle()
        }
      }
    )
  })
}

defineExpose({
  requestDataUpdate,
  restoreDefaults,
  forceUpdate
})

const isDisabled = computed(() => {
  const { input } = getResultTypeSettingsDisabledStates({
    isEdit: props.isEdit,
    editable: props.editable,
    isLockedOnPercent: props.isLockedOnPercent,
    isBinarySelectDisabled: props.isBinarySelectDisabled,
    valuesAreReadonly: props.valuesAreReadonly
  })

  return input
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/dnd';
@import '~@/assets/styles/result-type-settings';

/* calculate width same with `Start` or `Target` we calculating with without unit input and remove gap twice
  because we have gap between fields Start / Target and Target / Unit
 */
$major-field-width: calc(
  ((100% - #{$metric-unit-input-max-width}) - #{$fields-gap} - #{$fields-gap}) / 2
);

.epm-Wrapper {
  position: relative;
  display: flex;
  flex-direction: column;

  &:not(&-start-target) {
    margin-top: var(--padding-block, 0);

    &:before {
      content: '';
      position: absolute;
      left: calc(var(--padding-block, 0) * -1);
      width: calc(100% + (var(--padding-block, 0) * 2));
      top: 0;
      height: 2px;
      background-color: $grey-2-next;
    }
  }
}

.epm-PeriodsSwitch {
  @extend %width-styles;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding-top: var(--padding-block, 0);
}

.epm-PeriodsSwitch_Title {
  display: flex;
  align-items: center;
  gap: inherit;
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;

  &:not(.epm-Wrapper-readonly &) {
    color: $dark-2;
  }

  .epm-Wrapper-readonly & {
    color: $grey-1-next;
  }
}

.epm-ProgressSettings {
  @extend %width-styles;
  display: grid;
  grid-template-columns: $major-field-width 1fr;
  gap: $fields-gap;
  margin-top: var(--padding-block, 0);
  --button-text-font-weight: #{fw('semi-bold')};
  --disabled-opacity: 1;
  --button-disabled-color: #{$dark-3};
  --button-disabled-border-color: #{$grey-2-next};
  --button-disabled-background-color: #{$grey-2-next};
}

.epm-ProgressMilestonesHeader {
  @extend %width-styles;
  display: grid;
  grid-template-columns: $major-field-width 1fr;
  gap: $fields-gap;
  margin-bottom: 6px;
}

.epm-ProgressMilestones {
  @extend %width-styles;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: $fields-gap;
}

.epm-Footer {
  @extend %width-styles;

  &:not(&:empty) {
    margin-top: 20px;
  }
}

.epm-Milestone {
  display: grid;
  grid-template-columns: $major-field-width 1fr;
  gap: inherit;
  align-items: flex-end;

  // &.stop {
  //   transform: translateY(-30px);
  // }

  &-moving /*,   &.stop */ {
    position: relative;
    @extend %grabbing-item-common-styles;
    animation-delay: 351ms;

    &:before {
      content: '';
      position: absolute;
      left: calc(var(--padding-block, 0) * -1);
      width: calc(100% + (var(--padding-block, 0) * 2));
      height: 100%;
      animation: pulse calc(var(--cancel-animation-delay, 1ms) / 2) ease-in-out infinite;
      z-index: 0;
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 $grey-3-next;
    background-color: $grey-3-next;
  }
  50% {
    box-shadow: 0 0 0 3px rgba($grade-high-color-next, 0.2);
    background-color: rgba($grade-high-color-next, 0.2);
  }
}

.emp-Milestone_TargetValueWrapper {
  width: 100%;
  display: flex;
  align-items: flex-end;
  gap: inherit;
  &:deep(.ain-InputWrapper) {
    width: 100%;
    flex-grow: 1;
  }
}

.epm-TargetValueInput {
  width: 100%;
  --font-size: #{$fs-14};
}

.emp-Milestone_RemoveWrapper {
  height: 40px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 1;
}

.epm-ProgressHeader {
  @extend %width-styles;
  display: flex;
  align-items: center;
  gap: $fields-gap;
  justify-content: space-between;
  margin-block: var(--padding-block, 0);
}

.epm-ProgressHeader_Title {
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:not(.epm-Wrapper-readonly &) {
    color: $dark-2;
  }

  .epm-Wrapper-readonly & {
    color: $grey-1-next;
  }
}

.epm-ActionsButton {
  &-active {
    background-color: $dark-2;
    color: $white;
  }
}

.epm-Divider {
  @extend %width-styles;
  margin-top: var(--padding-block, 0);
}

/*
.flip-list-move,
.flip-list-enter-active,
.flip-list-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.flip-list-enter-from,
.flip-list-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.flip-list-leave-active {
  position: absolute;
}*/

.epm-AddMilestone {
  --ab-link-dark-color: #{$dark-2};
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;
}

:deep(.ai-Icon.after) {
  .epm-AddMilestone & {
    margin-left: auto;
  }
}
</style>
